import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DocumentTable from "pages/dashboard/admin/prompts/components/DocumentTable";
import { compareByFileName } from "pages/dashboard/admin/prompts/components/documentListUtils";
import ConfirmDialog from "components/common/ConfirmDialog";
import { deleteDocument as deleteEmbeddedDocument } from "services/embeddedDocumentsService";
import { deleteDocument as deleteClinicalReference } from "services/clinicalReferencesService";
import { useAuth } from "hooks/useAuth";

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024)
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const AllFilesPage = () => {
  const { embeddedDocs, clinicalRefs, chartmindDocsLoading } = useAuth();

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Merge both collections into one row per file: a doc uploaded to both
  // Clinical References and Embedded Documents (same contentHash) shows once,
  // and deleting that row removes BOTH copies. `_targets` carries every
  // underlying doc so the delete handler can route each to its own service.
  const allFiles = useMemo(() => {
    const sources = [
      ...(embeddedDocs || []).map((doc) => ({ ...doc, _source: "embedded" })),
      ...(clinicalRefs || []).map((doc) => ({ ...doc, _source: "clinical" })),
    ];
    const byKey = new Map();
    for (const doc of sources) {
      const key = doc.contentHash || `${doc._source}:${doc.id}`;
      const target = { source: doc._source, id: doc.id };
      const existing = byKey.get(key);
      if (existing) {
        existing._targets.push(target);
        existing._totalBytes += Number(doc.fileSize) || 0;
        existing.promptIds = Array.from(
          new Set([
            ...existing.promptIds,
            ...(Array.isArray(doc.promptIds) ? doc.promptIds : []),
          ]),
        );
      } else {
        byKey.set(key, {
          ...doc,
          id: key, // stable row key across both collections
          promptIds: Array.isArray(doc.promptIds) ? [...doc.promptIds] : [],
          _targets: [target],
          _totalBytes: Number(doc.fileSize) || 0,
        });
      }
    }
    return Array.from(byKey.values()).sort(compareByFileName);
  }, [embeddedDocs, clinicalRefs]);

  // True disk usage: counts every stored copy, including merged duplicates.
  const totalBytes = useMemo(
    () => allFiles.reduce((sum, doc) => sum + doc._totalBytes, 0),
    [allFiles],
  );

  const visibleFiles = useMemo(() => {
    const needle = normalize(search);
    if (!needle) return allFiles;
    return allFiles.filter((doc) =>
      [doc.fileName, ...doc.promptIds].some((value) =>
        normalize(value).includes(needle),
      ),
    );
  }, [allFiles, search]);

  const toggleSelected = (docId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedDocs = useMemo(
    () => allFiles.filter((doc) => selectedIds.has(doc.id)),
    [allFiles, selectedIds],
  );

  // This page is the single surface for permanent deletion. The prompt panels
  // only detach; here each file is destroyed everywhere: every underlying doc
  // (embedded and/or clinical), its chunks, and its Storage file.
  const handleConfirmDelete = async () => {
    if (!deleteTargets.length) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      // Batch by collection so each service gets one call.
      const embeddedIds = [];
      const clinicalIds = [];
      deleteTargets.forEach((doc) => {
        doc._targets.forEach((target) => {
          if (target.source === "clinical") clinicalIds.push(target.id);
          else embeddedIds.push(target.id);
        });
      });
      const results = await Promise.all([
        embeddedIds.length
          ? deleteEmbeddedDocument(embeddedIds, null, { everywhere: true })
          : null,
        clinicalIds.length
          ? deleteClinicalReference(clinicalIds, null, { everywhere: true })
          : null,
      ]);
      const firstError = results
        .filter(Boolean)
        .flatMap((result) => result?.errors || [])
        .map((entry) => entry.error)
        .find(Boolean);
      if (firstError) {
        setError(`Some files could not be deleted: ${firstError}`);
      } else {
        setSuccess(
          `Permanently deleted ${deleteTargets.length} ${deleteTargets.length === 1 ? "file" : "files"}.`,
        );
      }
      setSelectedIds((current) => {
        const next = new Set(current);
        deleteTargets.forEach((doc) => next.delete(doc.id));
        return next;
      });
      setDeleteTargets([]);
    } catch (err) {
      console.error("[AllFilesPage] delete failed:", err);
      setError("Delete failed: " + (err.message || "Unknown error"));
    } finally {
      setDeleting(false);
    }
  };

  const deleteCount = deleteTargets.length;
  const deletePromptIds = useMemo(
    () =>
      Array.from(new Set(deleteTargets.flatMap((doc) => doc.promptIds))),
    [deleteTargets],
  );

  return (
    <Box>
      <DashboardPageHeader
        title="All Files"
        subtitle={
          chartmindDocsLoading
            ? "Every document in your library, across all prompts."
            : `Every document in your library, across all prompts. ${allFiles.length} ${allFiles.length === 1 ? "file" : "files"}, ${formatBytes(totalBytes)} total. Deleting here permanently removes the file from every prompt.`
        }
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
          minHeight: 40,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by file name or prompt"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />
        {selectedIds.size > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, py: 0.25 }}
              onClick={clearSelection}
              disabled={deleting}
            >
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              sx={{ px: 1, py: 0.25 }}
              onClick={() => setDeleteTargets(selectedDocs)}
              disabled={deleting}
            >
              Delete selected
            </Button>
          </Stack>
        ) : null}
      </Box>
      <DocumentTable
        documents={visibleFiles}
        loading={chartmindDocsLoading}
        error={null}
        emptyMessage={
          allFiles.length
            ? "No files match your search."
            : "No uploaded files found for this organization."
        }
        selection={{ selectedIds, onToggle: toggleSelected }}
        getEndAdornment={(doc) => formatBytes(doc._totalBytes)}
        getSecondaryDetails={(doc) =>
          doc.promptIds.length
            ? `Used by: ${doc.promptIds.join(", ")}`
            : "Not attached to any prompt"
        }
        onRemove={(doc) => setDeleteTargets([doc])}
        removeLabel="Delete"
        maxHeight="none"
      />
      <ConfirmDialog
        open={deleteCount > 0}
        onClose={() => {
          if (!deleting) setDeleteTargets([]);
        }}
        title={`Permanently delete ${deleteCount === 1 ? "this file" : `${deleteCount} files`}?`}
        message={
          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="body2">
              {deleteCount === 1 ? (
                <>
                  <strong>{deleteTargets[0]?.fileName || "Untitled"}</strong> will
                  be permanently deleted from storage and removed from every
                  prompt that uses it
                  {deleteTargets[0]?._targets?.length > 1
                    ? ", including both its Clinical References and Embedded Documents copies"
                    : ""}
                  . This cannot be undone.
                </>
              ) : (
                <>
                  These {deleteCount} files will be permanently deleted from
                  storage and removed from every prompt that uses them, including
                  any Clinical References and Embedded Documents copies. This
                  cannot be undone.
                </>
              )}
            </Typography>
            {deleteCount > 1 && deleteCount <= 10 ? (
              <Typography variant="caption" color="text.secondary">
                {deleteTargets.map((doc) => doc.fileName || "Untitled").join(", ")}
              </Typography>
            ) : null}
            {deletePromptIds.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Used by {deletePromptIds.length}{" "}
                {deletePromptIds.length === 1 ? "prompt" : "prompts"}:{" "}
                {deletePromptIds.join(", ")}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Not attached to any prompt.
              </Typography>
            )}
          </Box>
        }
        confirmLabel="Delete permanently"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default AllFilesPage;
